import type { MoneyDecimal, PaymentOpenItemOrder } from '../../api/types';

const UNGROUPED_MONEY = /^\d+$/;
const INTERNATIONAL_MONEY = /^\d{1,3}(?:,\d{3})+$/;
const INDIAN_MONEY = /^\d{1,2}(?:,\d{2})*,\d{3}$/;

export function parsePaise(value: MoneyDecimal | null | undefined): bigint {
  const normalized = value?.trim() ?? '';
  const parts = normalized.split('.');
  if (parts.length > 2 || (parts[1] != null && !/^\d{1,2}$/.test(parts[1]))) {
    throw new Error('Enter a valid amount.');
  }
  const rupees = parts[0];
  if (![UNGROUPED_MONEY, INTERNATIONAL_MONEY, INDIAN_MONEY].some((pattern) => pattern.test(rupees))) {
    throw new Error('Enter a valid amount.');
  }
  const fraction = (parts[1] ?? '').padEnd(2, '0');
  return BigInt(rupees.replace(/,/g, '')) * 100n + BigInt(fraction || '0');
}

export function moneyInputError(value: string): string | null {
  try {
    parsePaise(value);
    return null;
  } catch {
    return 'Enter a valid amount.';
  }
}

export function inputPaise(value: string): bigint {
  return moneyInputError(value) ? 0n : parsePaise(value);
}

export function paiseToDecimal(paise: bigint): MoneyDecimal {
  const sign = paise < 0n ? '-' : '';
  const absolute = paise < 0n ? -paise : paise;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
}

export function formatMoney(paise: bigint): string {
  const sign = paise < 0n ? '-' : '';
  const absolute = paise < 0n ? -paise : paise;
  const rupees = String(absolute / 100n);
  const tail = rupees.length > 3 ? rupees.slice(-3) : rupees;
  let head = rupees.length > 3 ? rupees.slice(0, -3) : '';
  const groups: string[] = [];
  while (head.length > 2) {
    groups.unshift(head.slice(-2));
    head = head.slice(0, -2);
  }
  if (head) groups.unshift(head);
  const grouped = groups.length ? `${groups.join(',')},${tail}` : tail;
  return `${sign}₹${grouped}.${String(absolute % 100n).padStart(2, '0')}`;
}

export interface AllocationValue {
  amount: MoneyDecimal;
  tdsAmount: MoneyDecimal;
}

export function allocationTotals(lines: AllocationValue[], paymentAmount: MoneyDecimal) {
  const totalCashPaise = lines.reduce((sum, line) => sum + parsePaise(line.amount), 0n);
  const totalTdsPaise = lines.reduce((sum, line) => sum + parsePaise(line.tdsAmount), 0n);
  const creditPaise = parsePaise(paymentAmount) - totalCashPaise;
  return { totalCashPaise, totalTdsPaise, creditPaise, isOver: creditPaise < 0n };
}

export interface ReceiptAllocationRow {
  shipmentId: number;
  orderDate?: string | null;
  outstandingAmount: MoneyDecimal;
  amount: MoneyDecimal;
  tdsAmount: MoneyDecimal;
}

export function fillOldestFirst(
  rows: ReceiptAllocationRow[],
  paymentAmount: MoneyDecimal,
): ReceiptAllocationRow[] {
  let remaining = parsePaise(paymentAmount);
  const amounts = new Map<number, MoneyDecimal>();
  const ordered = [...rows].sort((left, right) => {
    const leftDate = left.orderDate ?? '9999-12-31';
    const rightDate = right.orderDate ?? '9999-12-31';
    return leftDate.localeCompare(rightDate) || left.shipmentId - right.shipmentId;
  });
  ordered.forEach((row) => {
    const availableForCash = parsePaise(row.outstandingAmount) - parsePaise(row.tdsAmount);
    const amount = availableForCash > 0n ? (availableForCash < remaining ? availableForCash : remaining) : 0n;
    remaining -= amount;
    amounts.set(row.shipmentId, paiseToDecimal(amount));
  });
  return rows.map((row) => ({ ...row, amount: amounts.get(row.shipmentId) ?? '0.00' }));
}

export function fillAllDue(orders: Pick<PaymentOpenItemOrder, 'shipmentId' | 'advanceRemaining' | 'balanceRemaining' | 'advanceAvailable'>[]) {
  return orders.map((order) => ({
    shipmentId: order.shipmentId,
    advance: paiseToDecimal(
      parsePaise(order.advanceAvailable) > 0n ? parsePaise(order.advanceRemaining) : 0n,
    ),
    balance: paiseToDecimal(parsePaise(order.balanceRemaining)),
    tdsAmount: '0.00',
  }));
}
